// src/audio/types.ts
var DEFAULT_CONTINUOUS_VAD_CONFIG = {
  energy_threshold_db: -45,
  min_frames_above: 3,
  fft_size: 2048,
  smoothing: 0.8,
  min_speech_duration_ms: 100,
  speech_end_debounce_ms: 200,
  adaptive_floor_enabled: true,
  calibration_window_ms: 400,
  calibration_min_samples: 6,
  calibration_percentile: 0.25,
  adaptive_margin_db: 9,
  post_voice1_grace_ms: 1500,
  prompt_boundary_guard_ms: 250
};
function createEmptySpeechTimingResult(promptEndMs, voice1StartMs) {
  return {
    prompt_start_ms: 0,
    prompt_end_ms: promptEndMs,
    voice1_start_ms: voice1StartMs,
    speech_start_ms: null,
    speech_end_ms: null,
    response_latency_ms: null,
    learner_duration_ms: null,
    duration_delta_ms: null,
    started_during_prompt: false,
    still_speaking_at_voice1: false,
    speech_detected: false,
    peak_energy_db: -100,
    average_energy_db: -100
  };
}

// src/config/defaults.ts
var DEFAULT_VAD_CONFIG = {
  energy_threshold_db: -45,
  min_frames_above: 3,
  fft_size: 2048,
  smoothing: 0.8
};

// src/audio/envelope-extractor-v1.json
var envelope_extractor_v1_default = {
  version: 1,
  gridMs: 20,
  smoothingTaps: 3,
  smoothingWindowMs: 60,
  peakProminenceRatio: 0.25,
  peakMinSeparationMs: 120,
  minSampleCount: 10
};

// src/audio/envelopeMetadata.ts
var ENVELOPE_EXTRACTOR_CONSTANTS = envelope_extractor_v1_default;
var CONTOUR_MAX_POINTS = 128;
function compactContour(envelope, gridMs, maxPoints = CONTOUR_MAX_POINTS) {
  const n = envelope.length;
  const points = Math.min(n, maxPoints);
  const contourGridMs = n * gridMs / points;
  const max = Math.max(...envelope);
  const contour = new Array(points).fill(0);
  if (max <= 0) return { contour, contourGridMs };
  for (let p = 0; p < points; p++) {
    const start = Math.floor(p * n / points);
    const end = Math.max(start + 1, Math.floor((p + 1) * n / points));
    let sum = 0;
    for (let i = start; i < end; i++) sum += envelope[i];
    contour[p] = Math.round(sum / (end - start) / max * 100);
  }
  return { contour, contourGridMs };
}
var emptyMetadata = (durationMs, sampleCount) => ({
  durationMs,
  peakCount: 0,
  peakToMeanRatio: 0,
  meanPeakWidthMs: 0,
  sampleCount,
  weight: 0
});
function resampleToGrid(samples, gridMs) {
  if (samples.length === 0) return new Float64Array(0);
  const sorted = [...samples].sort((a, b) => a.t - b.t);
  const start = sorted[0].t;
  const end = sorted[sorted.length - 1].t;
  const numPoints = Math.max(1, Math.floor((end - start) / gridMs) + 1);
  const out = new Float64Array(numPoints);
  let lo = 0;
  for (let g = 0; g < numPoints; g++) {
    const t = start + g * gridMs;
    while (lo < sorted.length - 2 && sorted[lo + 1].t < t) lo++;
    const a = sorted[lo];
    const b = sorted[Math.min(lo + 1, sorted.length - 1)];
    const dB = a.t === b.t ? a.db : a.db + (b.db - a.db) * (t - a.t) / (b.t - a.t);
    out[g] = Math.pow(10, dB / 20);
  }
  return out;
}
function movingAverage(series, taps) {
  const half = Math.floor(taps / 2);
  const out = new Float64Array(series.length);
  for (let i = 0; i < series.length; i++) {
    let sum = 0;
    let count = 0;
    for (let k = -half; k <= half; k++) {
      const j = i + k;
      if (j >= 0 && j < series.length) {
        sum += series[j];
        count++;
      }
    }
    out[i] = sum / count;
  }
  return out;
}
function prominenceOf(series, i) {
  const v = series[i];
  let leftMin = v;
  for (let j = i - 1; j >= 0; j--) {
    if (series[j] > v) break;
    if (series[j] < leftMin) leftMin = series[j];
  }
  let rightMin = v;
  for (let j = i + 1; j < series.length; j++) {
    if (series[j] > v) break;
    if (series[j] < rightMin) rightMin = series[j];
  }
  return v - Math.max(leftMin, rightMin);
}
function widthAt(series, i, threshold) {
  let left = i;
  while (left > 0 && series[left - 1] >= threshold) left--;
  let right = i;
  while (right < series.length - 1 && series[right + 1] >= threshold) right++;
  return right - left;
}
function findLocalMaxima(series) {
  const maxima = [];
  for (let i = 0; i < series.length; i++) {
    const prev = i === 0 ? -Infinity : series[i - 1];
    const next = i === series.length - 1 ? -Infinity : series[i + 1];
    if (series[i] >= prev && series[i] >= next && (series[i] > prev || series[i] > next)) {
      maxima.push(i);
    }
  }
  return maxima;
}
function extractEnvelopeMetadata(samples, durationMs, constants = ENVELOPE_EXTRACTOR_CONSTANTS) {
  const { gridMs, smoothingTaps, peakProminenceRatio, peakMinSeparationMs, minSampleCount } = constants;
  const rawEnvelope = resampleToGrid(samples, gridMs);
  const sampleCount = rawEnvelope.length;
  if (sampleCount < minSampleCount) {
    return emptyMetadata(durationMs, sampleCount);
  }
  const envelope = movingAverage(rawEnvelope, smoothingTaps);
  const max = Math.max(...envelope);
  const mean2 = envelope.reduce((a, b) => a + b, 0) / envelope.length;
  const peakToMeanRatio = mean2 > 0 ? max / mean2 : 0;
  const prominenceThreshold = peakProminenceRatio * (max - mean2);
  const candidates = prominenceThreshold <= 0 ? [] : findLocalMaxima(envelope).map((i) => ({ i, prominence: prominenceOf(envelope, i) })).filter((c) => c.prominence >= prominenceThreshold).sort((a, b) => envelope[b.i] - envelope[a.i]);
  const minSeparationSteps = peakMinSeparationMs / gridMs;
  const accepted = [];
  for (const c of candidates) {
    if (accepted.every((a) => Math.abs(a.i - c.i) >= minSeparationSteps)) accepted.push(c);
  }
  accepted.sort((a, b) => a.i - b.i);
  const widths = accepted.map((c) => widthAt(envelope, c.i, envelope[c.i] - c.prominence / 2) * gridMs);
  const meanPeakWidthMs = widths.length ? widths.reduce((a, b) => a + b, 0) / widths.length : 0;
  const { contour, contourGridMs } = compactContour(envelope, gridMs);
  return {
    durationMs,
    peakCount: accepted.length,
    peakToMeanRatio,
    meanPeakWidthMs,
    sampleCount,
    weight: 1,
    contour,
    contourGridMs
  };
}

// src/audio/VoiceActivityDetector.ts
var VoiceActivityDetector = class {
  config;
  audioContext = null;
  analyser = null;
  mediaStream = null;
  sourceNode = null;
  // Monitoring state
  isMonitoring = false;
  monitoringStartTime = 0;
  rafId = null;
  // Accumulated metrics during monitoring
  energySamples = [];
  speechFrameCount = 0;
  consecutiveAboveThreshold = 0;
  peakEnergy = -Infinity;
  totalSpeechDuration = 0;
  lastSpeechStartTime = null;
  // Continuous monitoring state (for timing analysis)
  isContinuousMode = false;
  continuousConfig = DEFAULT_CONTINUOUS_VAD_CONFIG;
  continuousStartTime = 0;
  phaseTimestamps = /* @__PURE__ */ new Map();
  firstSpeechStartAbsolute = null;
  // Absolute time of first speech start
  lastSpeechEndAbsolute = null;
  // Absolute time of last speech end
  speechEndDebounceTimer = null;
  confirmedSpeechEnd = null;
  // Confirmed end (after debounce)
  // Timestamped energy samples for continuous mode ONLY (adaptation v2 WP-6).
  // rAF cadence is display-locked/throttle-prone, so envelope extraction needs
  // timestamps, not just values. Never leaves this class — stopContinuousMonitoring
  // consumes it into EnvelopeMetadata (5 numbers) and discards it immediately.
  continuousEnergyTimeline = [];
  // Playback-rejection state (continuous mode only, 2026-08-20).
  // See ContinuousVADConfig for why the gate has to be relative.
  calibrationSamples = [];
  calibrationClosed = false;
  /** max(absolute threshold, measured floor + margin); null until calibrated */
  adaptiveThresholdDb = null;
  /** Start of an above-floor burst not yet sustained long enough to count.
   *  Re-armable: a burst that dies before min_speech_duration_ms is discarded,
   *  so a transient can no longer own the whole cycle's measurement. */
  candidateSpeechStart = null;
  /** Has the pending candidate stayed up for min_speech_duration_ms? Once it
   *  has, it is held until the prompt-end boundary can judge it, rather than
   *  being dropped the moment its run ends. */
  candidateSustained = false;
  /** Absolute time the pending candidate's run fell back to the floor; null
   *  while it is still above. A prompt-window candidate is judged on WHERE
   *  its run ended relative to PROMPT_END, and PROMPT_END is usually marked
   *  after the run has already ended, so that moment has to be remembered. */
  candidateRunEnd = null;
  /** Bumped by every dispose(); initialize() captures it before awaiting
   *  getUserMedia, so a dispose() that lands while the permission prompt is
   *  still up can be seen after the await. Without it the stream resolves into
   *  an owner that is already gone: the tracks are never stopped, the mic stays
   *  open, and on Android that alone holds the device in communication mode and
   *  kills Bluetooth A2DP media (learner report, 2026-09-22). */
  disposeGeneration = 0;
  constructor(config = {}) {
    this.config = { ...DEFAULT_VAD_CONFIG, ...config };
  }
  /**
   * Initialize VAD with microphone access
   * MUST be called from a user gesture (click, touch, etc.)
   * @returns true if initialization successful, false otherwise
   */
  async initialize() {
    if (this.isInitialized()) return true;
    if (typeof window === "undefined") {
      console.warn("VoiceActivityDetector: Not in browser environment");
      return false;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      console.warn("VoiceActivityDetector: getUserMedia not supported");
      return false;
    }
    try {
      const generationAtStart = this.disposeGeneration;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        },
        video: false
      }).catch((error) => {
        console.warn("VoiceActivityDetector: Failed to initialize", error);
        return null;
      });
      if (!stream) return false;
      if (this.disposeGeneration !== generationAtStart) {
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }
      if (this.isInitialized()) {
        stream.getTracks().forEach((track) => track.stop());
        return true;
      }
      this.mediaStream = stream;
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContextClass();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = this.config.fft_size;
      this.analyser.smoothingTimeConstant = this.config.smoothing;
      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.sourceNode.connect(this.analyser);
      return true;
    } catch (error) {
      console.warn("VoiceActivityDetector: Failed to initialize", error);
      this.dispose();
      return false;
    }
  }
  /**
   * Check if VAD is initialized and ready
   */
  isInitialized() {
    return this.audioContext !== null && this.analyser !== null;
  }
  /**
   * Start monitoring for voice activity
   * Call this at the beginning of PAUSE phase
   */
  startMonitoring() {
    if (!this.isInitialized()) {
      console.warn("VoiceActivityDetector: Not initialized, call initialize() first");
      return;
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.speechEndDebounceTimer) {
      clearTimeout(this.speechEndDebounceTimer);
      this.speechEndDebounceTimer = null;
    }
    this.isContinuousMode = false;
    this.isMonitoring = true;
    this.monitoringStartTime = performance.now();
    this.energySamples = [];
    this.speechFrameCount = 0;
    this.consecutiveAboveThreshold = 0;
    this.peakEnergy = -Infinity;
    this.totalSpeechDuration = 0;
    this.lastSpeechStartTime = null;
    if (this.audioContext?.state === "suspended") {
      this.audioContext.resume().catch((error) => {
        console.warn("VoiceActivityDetector: Failed to resume audio context", error);
      });
    }
    this.analyzeLoop();
  }
  /**
   * Stop monitoring and return results
   * Call this at the end of PAUSE phase
   */
  stopMonitoring() {
    const endTime = performance.now();
    this.isMonitoring = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.lastSpeechStartTime !== null) {
      this.totalSpeechDuration += endTime - this.lastSpeechStartTime;
    }
    const duration = endTime - this.monitoringStartTime;
    const averageEnergy = this.calculateAverageEnergy();
    const activityRatio = duration > 0 ? this.totalSpeechDuration / duration : 0;
    return {
      speech_detected: this.speechFrameCount > 0,
      speech_duration_ms: this.totalSpeechDuration,
      peak_energy_db: this.peakEnergy === -Infinity ? -100 : this.peakEnergy,
      average_energy_db: averageEnergy,
      activity_ratio: Math.min(1, activityRatio),
      start_time: this.monitoringStartTime,
      end_time: endTime
    };
  }
  /**
   * Get current VAD status for UI feedback
   */
  getStatus() {
    if (!this.isMonitoring || !this.analyser) {
      return {
        is_speaking: false,
        current_energy_db: -100,
        is_active: this.isMonitoring
      };
    }
    const energy = this.getCurrentEnergy();
    const isSpeaking = energy > this.config.energy_threshold_db;
    return {
      is_speaking: isSpeaking,
      current_energy_db: energy,
      is_active: true
    };
  }
  /**
   * Get current energy level in dB
   */
  getCurrentEnergy() {
    if (!this.analyser) return -100;
    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(dataArray);
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const normalized = dataArray[i] / 255;
      sum += normalized * normalized;
    }
    const rms = Math.sqrt(sum / dataArray.length);
    const db = rms > 0 ? 20 * Math.log10(rms) : -100;
    return db;
  }
  /**
   * Update configuration
   */
  updateConfig(config) {
    this.config = { ...this.config, ...config };
    if (this.analyser) {
      if (config.fft_size !== void 0) {
        this.analyser.fftSize = config.fft_size;
      }
      if (config.smoothing !== void 0) {
        this.analyser.smoothingTimeConstant = config.smoothing;
      }
    }
  }
  /**
   * Clean up resources
   */
  dispose() {
    this.disposeGeneration += 1;
    this.isMonitoring = false;
    this.isContinuousMode = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.speechEndDebounceTimer) {
      clearTimeout(this.speechEndDebounceTimer);
      this.speechEndDebounceTimer = null;
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }
    if (this.audioContext) {
      this.audioContext.close().catch((error) => {
        console.warn("VoiceActivityDetector: Failed to close audio context", error);
      });
      this.audioContext = null;
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
  }
  /**
   * Internal analysis loop using requestAnimationFrame
   */
  analyzeLoop() {
    if (!this.isMonitoring || !this.analyser) return;
    const energy = this.getCurrentEnergy();
    const now = performance.now();
    this.energySamples.push(energy);
    if (energy > this.peakEnergy) {
      this.peakEnergy = energy;
    }
    const isAboveThreshold = energy > this.config.energy_threshold_db;
    if (isAboveThreshold) {
      this.consecutiveAboveThreshold++;
      if (this.consecutiveAboveThreshold >= this.config.min_frames_above) {
        if (this.lastSpeechStartTime === null) {
          this.lastSpeechStartTime = now;
        }
        this.speechFrameCount++;
      }
    } else {
      if (this.lastSpeechStartTime !== null) {
        this.totalSpeechDuration += now - this.lastSpeechStartTime;
        this.lastSpeechStartTime = null;
      }
      this.consecutiveAboveThreshold = 0;
    }
    this.rafId = requestAnimationFrame(() => this.analyzeLoop());
  }
  /**
   * Calculate average energy from samples
   */
  calculateAverageEnergy() {
    if (this.energySamples.length === 0) return -100;
    const sum = this.energySamples.reduce((a, b) => a + b, 0);
    return sum / this.energySamples.length;
  }
  // ============================================
  // CONTINUOUS MONITORING (for timing analysis)
  // ============================================
  /**
   * Start continuous monitoring from PROMPT phase.
   * Call this at the beginning of the PROMPT phase.
   *
   * @param config Optional continuous monitoring config
   */
  startContinuousMonitoring(config) {
    if (!this.isInitialized()) {
      console.warn("VoiceActivityDetector: Not initialized, call initialize() first");
      return;
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.isContinuousMode = true;
    this.continuousConfig = { ...DEFAULT_CONTINUOUS_VAD_CONFIG, ...this.config, ...config };
    this.continuousStartTime = performance.now();
    this.phaseTimestamps.clear();
    this.phaseTimestamps.set("PROMPT", 0);
    this.firstSpeechStartAbsolute = null;
    this.lastSpeechEndAbsolute = null;
    this.confirmedSpeechEnd = null;
    this.continuousEnergyTimeline = [];
    this.calibrationSamples = [];
    this.calibrationClosed = false;
    this.adaptiveThresholdDb = null;
    this.candidateSpeechStart = null;
    this.candidateSustained = false;
    this.candidateRunEnd = null;
    if (this.speechEndDebounceTimer) {
      clearTimeout(this.speechEndDebounceTimer);
      this.speechEndDebounceTimer = null;
    }
    this.isMonitoring = true;
    this.monitoringStartTime = this.continuousStartTime;
    this.energySamples = [];
    this.speechFrameCount = 0;
    this.consecutiveAboveThreshold = 0;
    this.peakEnergy = -Infinity;
    this.totalSpeechDuration = 0;
    this.lastSpeechStartTime = null;
    if (this.audioContext?.state === "suspended") {
      this.audioContext.resume().catch((error) => {
        console.warn("VoiceActivityDetector: Failed to resume audio context", error);
      });
    }
    this.continuousAnalyzeLoop();
  }
  /**
   * Mark a phase transition during continuous monitoring.
   * Use this to record when phases start/end for overlap detection.
   *
   * @param phase The phase that is starting or a marker like 'PROMPT_END'
   * @param timestamp Optional absolute timestamp (defaults to now)
   */
  markPhaseTransition(phase, timestamp) {
    if (!this.isContinuousMode) {
      console.warn("VoiceActivityDetector: Not in continuous mode");
      return;
    }
    const absoluteTime = timestamp ?? performance.now();
    const relativeTime = absoluteTime - this.continuousStartTime;
    this.phaseTimestamps.set(phase, relativeTime);
  }
  /**
   * Check if currently in continuous monitoring mode
   */
  isContinuousMonitoring() {
    return this.isContinuousMode;
  }
  /**
   * Stop continuous monitoring and return timing results.
   *
   * @param modelDurationMs Duration of the model (target) audio in ms
   * @returns Full timing result with latency, duration delta, and overlap flags
   */
  stopContinuousMonitoring(modelDurationMs) {
    if (!this.isContinuousMode) {
      console.warn("VoiceActivityDetector: Not in continuous mode");
      return createEmptySpeechTimingResult(0, 0);
    }
    const endTime = performance.now();
    this.isMonitoring = false;
    this.isContinuousMode = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.speechEndDebounceTimer) {
      clearTimeout(this.speechEndDebounceTimer);
      this.speechEndDebounceTimer = null;
    }
    const promptEndMs = this.phaseTimestamps.get("PROMPT_END") ?? 0;
    const voice1StartMs = this.phaseTimestamps.get("VOICE_1") ?? 0;
    this.tryResolveCandidate(endTime - this.continuousStartTime, true);
    if (this.lastSpeechStartTime !== null && this.confirmedSpeechEnd === null) {
      const cycleEndMs = endTime - this.continuousStartTime;
      const hasVoice1 = this.phaseTimestamps.has("VOICE_1");
      this.confirmedSpeechEnd = hasVoice1 ? Math.min(cycleEndMs, voice1StartMs + this.continuousConfig.post_voice1_grace_ms) : cycleEndMs;
    }
    const speechStartMs = this.firstSpeechStartAbsolute !== null ? this.firstSpeechStartAbsolute - this.continuousStartTime : null;
    const speechEndMs = speechStartMs === null ? null : this.confirmedSpeechEnd ?? (this.lastSpeechEndAbsolute !== null ? this.lastSpeechEndAbsolute - this.continuousStartTime : null);
    const responseLatencyMs = speechStartMs;
    const learnerDurationMs = speechStartMs !== null && speechEndMs !== null ? speechEndMs - speechStartMs : null;
    const durationDeltaMs = learnerDurationMs !== null ? learnerDurationMs - modelDurationMs : null;
    const startedDuringPrompt = speechStartMs !== null && speechStartMs < promptEndMs;
    const stillSpeakingAtVoice1 = speechEndMs !== null && speechEndMs > voice1StartMs;
    const averageEnergy = this.calculateAverageEnergy();
    let envelope;
    if (speechStartMs !== null && speechEndMs !== null && learnerDurationMs !== null) {
      const speechWindow = this.continuousEnergyTimeline.filter(
        (s) => s.t - this.continuousStartTime >= speechStartMs && s.t - this.continuousStartTime <= speechEndMs
      );
      envelope = extractEnvelopeMetadata(speechWindow, learnerDurationMs);
    }
    this.continuousEnergyTimeline = [];
    return {
      prompt_start_ms: 0,
      prompt_end_ms: promptEndMs,
      voice1_start_ms: voice1StartMs,
      speech_start_ms: speechStartMs,
      speech_end_ms: speechEndMs,
      response_latency_ms: responseLatencyMs,
      learner_duration_ms: learnerDurationMs,
      duration_delta_ms: durationDeltaMs,
      started_during_prompt: startedDuringPrompt,
      still_speaking_at_voice1: stillSpeakingAtVoice1,
      speech_detected: speechStartMs !== null,
      peak_energy_db: this.peakEnergy === -Infinity ? -100 : this.peakEnergy,
      average_energy_db: averageEnergy,
      envelope
    };
  }
  /**
   * The energy a sample must exceed to count as speech, right now.
   *
   * Until the calibration slice closes this is the configured absolute
   * threshold; afterwards it is the larger of that and (measured floor +
   * margin). The measured floor is a LOW QUANTILE of the calibration slice
   * (see `calibration_percentile`), not a mean, median or max: a learner
   * speaking during the slice adds energy on top of the playback and can only
   * push samples up, so the estimator has to survive upward contamination —
   * and the median does not survive enough of it.
   */
  currentSpeechThresholdDb() {
    const absolute = this.continuousConfig.energy_threshold_db;
    if (this.adaptiveThresholdDb === null) return absolute;
    return Math.max(absolute, this.adaptiveThresholdDb);
  }
  /**
   * Accumulate the calibration slice and close it once the window elapses (or
   * PROMPT_END arrives first, for a very short prompt). Returns true while
   * calibration is still open, during which no onset may be CONFIRMED — but
   * see `backdateOnsetThroughCalibration`, which recovers an onset that was
   * already under way when the slice closed.
   */
  updateCalibration(relativeNow, energy) {
    if (!this.continuousConfig.adaptive_floor_enabled) return false;
    if (this.calibrationClosed) return false;
    const promptEnd = this.phaseTimestamps.get("PROMPT_END");
    const windowElapsed = relativeNow >= this.continuousConfig.calibration_window_ms;
    const promptEnded = promptEnd !== void 0 && relativeNow >= promptEnd;
    if (!windowElapsed && !promptEnded) {
      this.calibrationSamples.push(energy);
      return true;
    }
    this.calibrationClosed = true;
    if (this.calibrationSamples.length >= this.continuousConfig.calibration_min_samples) {
      const sorted = [...this.calibrationSamples].sort((a, b) => a - b);
      const q = Math.min(Math.max(this.continuousConfig.calibration_percentile, 0), 1);
      const idx = Math.min(sorted.length - 1, Math.floor(q * sorted.length));
      this.adaptiveThresholdDb = sorted[idx] + this.continuousConfig.adaptive_margin_db;
      this.backdateOnsetThroughCalibration();
    }
    return false;
  }
  /**
   * Recover the onset of an utterance that was ALREADY IN PROGRESS when the
   * calibration slice closed.
   *
   * The slice cannot judge its own samples as they arrive — there is no floor
   * yet to judge them against. But the moment the floor exists, the slice can
   * be re-read: walk backwards from its last sample while the energy is above
   * the freshly measured floor, and the start of that run is when the learner
   * actually began. Without this, the most confident speaker there is — the
   * one who answers 200ms in because they already knew it — was credited at
   * the moment calibration happened to close, and that inflated latency was
   * reported as truth (measured: onset 200ms, reported 432ms).
   *
   * Only ever called with a measured floor in hand. Under the starved-
   * calibration fallback there is no floor, the absolute threshold governs,
   * and back-dating there would hand the whole cycle back to playback — which
   * is the original bug, so it is deliberately not done.
   *
   * The candidate seeded here is not yet an onset: it still has to clear the
   * sustain test and the prompt-boundary test like any other.
   */
  backdateOnsetThroughCalibration() {
    if (this.firstSpeechStartAbsolute !== null) return;
    if (this.candidateSpeechStart !== null) return;
    const threshold = this.currentSpeechThresholdDb();
    const timeline = this.continuousEnergyTimeline;
    if (timeline.length === 0) return;
    if (timeline[timeline.length - 1].db <= threshold) return;
    let i = timeline.length - 1;
    while (i > 0 && timeline[i - 1].db > threshold) i--;
    this.candidateSpeechStart = timeline[i].t;
    this.candidateSustained = false;
    this.candidateRunEnd = null;
  }
  /**
   * Resolve a sustained candidate whose onset lies inside the prompt window,
   * using the prompt-end boundary rather than level. See
   * `prompt_boundary_guard_ms` for why level alone cannot do this.
   *
   * @param relativeNow  now, relative to prompt start
   * @param stillAbove   is the run still above the floor at `relativeNow`?
   * @returns 'learner' to confirm, 'bleed' to discard and re-arm, 'pending'
   *          to keep waiting for the boundary to arrive.
   */
  resolvePromptWindowCandidate(relativeNow, stillAbove) {
    const promptEnd = this.phaseTimestamps.get("PROMPT_END");
    if (promptEnd === void 0) return "pending";
    const guard = this.continuousConfig.prompt_boundary_guard_ms;
    if (guard <= 0) return "learner";
    if (stillAbove) {
      return relativeNow >= promptEnd + guard ? "learner" : "pending";
    }
    if (relativeNow < promptEnd - guard) return "learner";
    return "bleed";
  }
  /**
   * Whether a NEW utterance may still begin. The learner is invited to speak
   * between PROMPT_END and VOICE_1; once the app starts playing target audio
   * an onset is far likelier to be that audio than a response, and the
   * response-latency measurement it would produce is meaningless anyway.
   * Speech already in progress is unaffected — which is what keeps
   * `still_speaking_at_voice1` measurable.
   */
  onsetWindowOpen(relativeNow) {
    const voice1 = this.phaseTimestamps.get("VOICE_1");
    return voice1 === void 0 || relativeNow < voice1;
  }
  /** Has the pending candidate stayed up long enough to be an utterance
   *  rather than a door, a tap or a click? */
  candidateIsSustained(now) {
    return this.candidateSpeechStart !== null && now - this.candidateSpeechStart >= this.continuousConfig.min_speech_duration_ms;
  }
  /**
   * Decide, with whatever is known right now, whether the pending sustained
   * candidate is the learner, the app's own prompt audio, or still
   * undecidable. Called every frame while a sustained candidate is pending,
   * and once more at stop.
   *
   * PROMPT_END is marked by the player when the prompt audio actually
   * finishes, which is normally LONG after the candidate began — so this
   * cannot be settled when the candidate is created. It is settled here, on
   * whichever frame the information finally exists.
   *
   * @param relativeNow now, relative to prompt start
   * @param atStop      final call: no more information is coming, so an
   *                    unmarked PROMPT_END means the boundary test is simply
   *                    unavailable and the candidate is taken at face value.
   */
  tryResolveCandidate(relativeNow, atStop = false) {
    if (this.firstSpeechStartAbsolute !== null) return;
    if (this.candidateSpeechStart === null || !this.candidateSustained) return;
    const promptEnd = this.phaseTimestamps.get("PROMPT_END");
    if (promptEnd === void 0) {
      if (atStop) this.confirmOnset();
      return;
    }
    const candidateRel = this.candidateSpeechStart - this.continuousStartTime;
    if (candidateRel >= promptEnd) {
      this.confirmOnset();
      return;
    }
    const stillAbove = this.candidateRunEnd === null;
    const at = stillAbove ? relativeNow : this.candidateRunEnd - this.continuousStartTime;
    const verdict = this.resolvePromptWindowCandidate(at, stillAbove);
    if (verdict === "learner") {
      this.confirmOnset();
    } else if (verdict === "bleed") {
      this.discardCandidate();
    }
  }
  /** Throw the pending candidate away and re-arm for a real response. */
  discardCandidate() {
    this.candidateSpeechStart = null;
    this.candidateSustained = false;
    this.candidateRunEnd = null;
  }
  /** Promote the pending candidate to THE onset for this cycle. */
  confirmOnset() {
    const onset = this.candidateSpeechStart;
    this.firstSpeechStartAbsolute = onset;
    const onsetRel = onset !== null ? onset - this.continuousStartTime : 0;
    if (this.lastSpeechEndAbsolute !== null && this.lastSpeechEndAbsolute <= (onset ?? 0)) {
      this.lastSpeechEndAbsolute = null;
    }
    if (this.confirmedSpeechEnd !== null && this.confirmedSpeechEnd <= onsetRel) {
      this.confirmedSpeechEnd = null;
    }
    if (this.lastSpeechEndAbsolute === null && this.speechEndDebounceTimer) {
      clearTimeout(this.speechEndDebounceTimer);
      this.speechEndDebounceTimer = null;
    }
  }
  /**
   * Internal analysis loop for continuous monitoring.
   * Similar to analyzeLoop but tracks first speech start and debounces speech end.
   */
  continuousAnalyzeLoop() {
    if (!this.isMonitoring || !this.analyser) return;
    const energy = this.getCurrentEnergy();
    const now = performance.now();
    const relativeNow = now - this.continuousStartTime;
    this.energySamples.push(energy);
    this.continuousEnergyTimeline.push({ t: now, db: energy });
    if (energy > this.peakEnergy) {
      this.peakEnergy = energy;
    }
    const calibrating = this.updateCalibration(relativeNow, energy);
    const isAboveThreshold = !calibrating && energy > this.currentSpeechThresholdDb();
    if (isAboveThreshold) {
      this.consecutiveAboveThreshold++;
      if (this.speechEndDebounceTimer) {
        clearTimeout(this.speechEndDebounceTimer);
        this.speechEndDebounceTimer = null;
      }
      if (this.consecutiveAboveThreshold >= this.continuousConfig.min_frames_above) {
        if (this.lastSpeechStartTime === null) {
          this.lastSpeechStartTime = now;
        }
        this.speechFrameCount++;
        if (this.firstSpeechStartAbsolute === null) {
          if (this.candidateSpeechStart === null) {
            if (this.onsetWindowOpen(relativeNow)) {
              this.candidateSpeechStart = this.lastSpeechStartTime;
              this.candidateSustained = false;
              this.candidateRunEnd = null;
            }
          } else if (this.candidateRunEnd === null && this.candidateIsSustained(now)) {
            this.candidateSustained = true;
          }
          this.tryResolveCandidate(relativeNow);
        }
      }
    } else {
      if (this.firstSpeechStartAbsolute === null && this.candidateSpeechStart !== null) {
        if (!this.candidateSustained) {
          this.discardCandidate();
        } else {
          if (this.candidateRunEnd === null) this.candidateRunEnd = now;
          this.tryResolveCandidate(relativeNow);
        }
      }
      if (this.lastSpeechStartTime !== null) {
        this.lastSpeechEndAbsolute = now;
        if (!this.speechEndDebounceTimer) {
          this.speechEndDebounceTimer = setTimeout(() => {
            if (this.lastSpeechEndAbsolute !== null) {
              this.confirmedSpeechEnd = this.lastSpeechEndAbsolute - this.continuousStartTime;
            }
            this.speechEndDebounceTimer = null;
          }, this.continuousConfig.speech_end_debounce_ms);
        }
        this.totalSpeechDuration += now - this.lastSpeechStartTime;
        this.lastSpeechStartTime = null;
      }
      this.consecutiveAboveThreshold = 0;
    }
    this.rafId = requestAnimationFrame(() => this.continuousAnalyzeLoop());
  }
};
function createVoiceActivityDetector(config) {
  return new VoiceActivityDetector(config);
}

// src/audio/SpeechTimingAnalyzer.ts
var SpeechTimingAnalyzer = class {
  vad;
  config;
  listeners = /* @__PURE__ */ new Set();
  isActive = false;
  lastResult = null;
  constructor(vad, config) {
    this.vad = vad;
    this.config = config ?? {};
  }
  /**
   * Start timing analysis for a new learning cycle.
   * Call this when PROMPT phase begins.
   */
  startCycle() {
    if (this.isActive) {
      console.warn("SpeechTimingAnalyzer: Cycle already active, stopping previous");
      this.reset();
    }
    if (!this.vad.isInitialized()) {
      console.warn("SpeechTimingAnalyzer: VAD not initialized");
      return;
    }
    this.isActive = true;
    this.lastResult = null;
    this.vad.startContinuousMonitoring(this.config);
    this.emit({ type: "cycle_started" });
  }
  /**
   * Record a phase transition.
   * Call this when phases change during the cycle.
   *
   * @param phase The phase that is starting, or 'PROMPT_END' when prompt audio ends
   * @param timestamp Optional timestamp (defaults to now)
   */
  onPhaseChange(phase, timestamp) {
    if (!this.isActive) {
      return;
    }
    this.vad.markPhaseTransition(phase, timestamp);
  }
  /**
   * End the timing analysis cycle and get results.
   *
   * @param modelDurationMs Duration of the target audio (for duration delta calculation)
   * @returns Full timing result with latency, overlap flags, etc.
   */
  endCycle(modelDurationMs) {
    if (!this.isActive) {
      console.warn("SpeechTimingAnalyzer: No active cycle");
      return createEmptySpeechTimingResult(0, 0);
    }
    const result = this.vad.stopContinuousMonitoring(modelDurationMs);
    this.isActive = false;
    this.lastResult = result;
    this.emit({ type: "cycle_ended", result });
    return result;
  }
  /**
   * Reset the analyzer (abort current cycle if active).
   */
  reset() {
    if (this.isActive && this.vad.isContinuousMonitoring()) {
      this.vad.stopContinuousMonitoring(0);
    }
    this.isActive = false;
    this.lastResult = null;
  }
  /**
   * Check if a timing cycle is currently active.
   */
  isAnalyzing() {
    return this.isActive;
  }
  /**
   * Get the last timing result (from previous cycle).
   */
  getLastResult() {
    return this.lastResult;
  }
  /**
   * Update configuration for future cycles.
   */
  updateConfig(config) {
    this.config = { ...this.config, ...config };
  }
  /**
   * Add an event listener.
   */
  addEventListener(listener) {
    this.listeners.add(listener);
  }
  /**
   * Remove an event listener.
   */
  removeEventListener(listener) {
    this.listeners.delete(listener);
  }
  /**
   * Emit an event to all listeners.
   */
  emit(event) {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch (error) {
        console.error("SpeechTimingAnalyzer: Listener error", error);
      }
    }
  }
};
function createSpeechTimingAnalyzer(vad, config) {
  return new SpeechTimingAnalyzer(vad, config);
}

// src/audio/PronunciationEngine.ts
var PROSODY_WEIGHTS = {
  // Tonal — envelope shape matters most (tone = energy contour)
  cmn: { duration: 0.2, peakCount: 0.3, envelope: 0.5 },
  zho: { duration: 0.2, peakCount: 0.3, envelope: 0.5 },
  yue: { duration: 0.2, peakCount: 0.3, envelope: 0.5 },
  tha: { duration: 0.25, peakCount: 0.3, envelope: 0.45 },
  vie: { duration: 0.25, peakCount: 0.3, envelope: 0.45 },
  // Pitch-accent
  jpn: { duration: 0.25, peakCount: 0.35, envelope: 0.4 },
  kor: { duration: 0.25, peakCount: 0.35, envelope: 0.4 },
  // Stress-timed — peak count and envelope both matter
  eng: { duration: 0.25, peakCount: 0.35, envelope: 0.4 },
  deu: { duration: 0.25, peakCount: 0.35, envelope: 0.4 },
  nld: { duration: 0.25, peakCount: 0.35, envelope: 0.4 },
  rus: { duration: 0.25, peakCount: 0.35, envelope: 0.4 },
  // Syllable-timed — duration and peak count matter most
  spa: { duration: 0.3, peakCount: 0.4, envelope: 0.3 },
  fra: { duration: 0.3, peakCount: 0.4, envelope: 0.3 },
  ita: { duration: 0.3, peakCount: 0.4, envelope: 0.3 },
  por: { duration: 0.3, peakCount: 0.4, envelope: 0.3 },
  // Celtic
  cym: { duration: 0.3, peakCount: 0.35, envelope: 0.35 },
  gle: { duration: 0.3, peakCount: 0.35, envelope: 0.35 },
  gla: { duration: 0.3, peakCount: 0.35, envelope: 0.35 },
  // Semitic
  ara: { duration: 0.3, peakCount: 0.35, envelope: 0.35 },
  heb: { duration: 0.3, peakCount: 0.35, envelope: 0.35 }
};
var DEFAULT_WEIGHTS = { duration: 0.25, peakCount: 0.35, envelope: 0.4 };
function getWeightsForLanguage(langCode) {
  return Object.prototype.hasOwnProperty.call(PROSODY_WEIGHTS, langCode) ? PROSODY_WEIGHTS[langCode] : DEFAULT_WEIGHTS;
}
var ANALYSIS_FPS = 50;
function extractPitchContour(audioBuffer) {
  const channelData = audioBuffer.numberOfChannels > 1 ? mixToMono(audioBuffer) : audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;
  const frameSize = Math.round(sampleRate / ANALYSIS_FPS);
  const times = [];
  const frequencies = [];
  const clarities = [];
  const energy = [];
  for (let offset = 0; offset + frameSize <= channelData.length; offset += frameSize) {
    let sumSq = 0;
    for (let i = offset; i < offset + frameSize; i++) {
      sumSq += channelData[i] * channelData[i];
    }
    const rms = Math.sqrt(sumSq / frameSize);
    times.push(offset / sampleRate);
    frequencies.push(0);
    clarities.push(rms > 0.01 ? 0.95 : 0.1);
    energy.push(rms);
  }
  return { times, frequencies, clarities, sampleRate, energy };
}
function mixToMono(buffer) {
  const length = buffer.length;
  const mono = new Float32Array(length);
  const numChannels = buffer.numberOfChannels;
  for (let ch = 0; ch < numChannels; ch++) {
    const channelData = buffer.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      mono[i] += channelData[i] / numChannels;
    }
  }
  return mono;
}
function trimSilence(energy) {
  if (energy.length === 0) return [0, 0];
  const peak = Math.max(...energy);
  const threshold = Math.max(peak * 0.25, 8e-3);
  let start = 0;
  while (start < energy.length && energy[start] < threshold) start++;
  let end = energy.length;
  while (end > start && energy[end - 1] < threshold) end--;
  if (start >= end) return [0, energy.length];
  return [start, end];
}
function countEnergyPeaks(energy) {
  if (energy.length < 3) return energy.length > 0 ? 1 : 0;
  const smoothed = smooth(energy, 7);
  const sorted = [...smoothed].sort((a, b) => a - b);
  const p70 = sorted[Math.floor(sorted.length * 0.7)] || 0;
  const threshold = Math.max(p70 * 0.6, sorted[Math.floor(sorted.length * 0.3)] || 0);
  let peaks = 0;
  for (let i = 1; i < smoothed.length - 1; i++) {
    if (smoothed[i] > smoothed[i - 1] && smoothed[i] > smoothed[i + 1] && smoothed[i] > threshold) {
      peaks++;
      i += 4;
    }
  }
  return Math.max(1, peaks);
}
function smooth(arr, windowSize) {
  const half = Math.floor(windowSize / 2);
  return arr.map((_, i) => {
    let sum = 0, count = 0;
    for (let j = Math.max(0, i - half); j <= Math.min(arr.length - 1, i + half); j++) {
      sum += arr[j];
      count++;
    }
    return sum / count;
  });
}
function normalizeEnvelope(energy, bins = 20) {
  if (energy.length === 0) return new Array(bins).fill(1 / bins);
  const actualBins = Math.min(bins, energy.length);
  const binSize = energy.length / actualBins;
  const result = [];
  for (let b = 0; b < actualBins; b++) {
    const start = Math.floor(b * binSize);
    const end = Math.max(start + 1, Math.floor((b + 1) * binSize));
    let sum = 0;
    for (let i = start; i < Math.min(end, energy.length); i++) {
      sum += energy[i];
    }
    result.push(sum / (end - start));
  }
  while (result.length < bins) {
    result.push(result[result.length - 1] || 0);
  }
  const total = result.reduce((a, b) => a + b, 0);
  if (total > 0) {
    for (let i = 0; i < result.length; i++) result[i] /= total;
  }
  return result;
}
function envelopeSimilarity(a, b) {
  if (a.length !== b.length || a.length === 0) return 50;
  let dotProduct = 0, normA = 0, normB = 0;
  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  if (denom === 0) return 50;
  const cosSim = dotProduct / denom;
  return Math.max(0, Math.min(100, cosSim * 100));
}
function compareProsody(nativeContour, learnerContour, langCode) {
  const weights = getWeightsForLanguage(langCode);
  const [nStart, nEnd] = trimSilence(nativeContour.energy);
  const [lStart, lEnd] = trimSilence(learnerContour.energy);
  const nEnergy = nativeContour.energy.slice(nStart, nEnd);
  const lEnergy = learnerContour.energy.slice(lStart, lEnd);
  const nDuration = nEnergy.length / ANALYSIS_FPS;
  const lDuration = lEnergy.length / ANALYSIS_FPS;
  let durationScore = 50;
  if (nDuration > 0 && lDuration > 0) {
    const ratio = lDuration / nDuration;
    const deviation = Math.abs(1 - ratio);
    durationScore = Math.max(0, Math.min(100, 100 * Math.exp(-1.5 * deviation * deviation)));
  }
  const nPeaks = countEnergyPeaks(nEnergy);
  const lPeaks = countEnergyPeaks(lEnergy);
  let peakCountScore;
  if (nPeaks === lPeaks) {
    peakCountScore = 100;
  } else {
    const diff = Math.abs(nPeaks - lPeaks);
    peakCountScore = Math.max(0, Math.min(100, 100 * Math.exp(-0.3 * diff * diff)));
  }
  const nNorm = normalizeEnvelope(nEnergy);
  const lNorm = normalizeEnvelope(lEnergy);
  const envelopeScore = envelopeSimilarity(nNorm, lNorm);
  const overall = Math.round(
    weights.duration * durationScore + weights.peakCount * peakCountScore + weights.envelope * envelopeScore
  );
  return {
    score: {
      duration: Math.round(durationScore),
      peakCount: Math.round(peakCountScore),
      envelope: Math.round(envelopeScore),
      overall
    },
    nativeContour: nNorm,
    learnerContour: lNorm,
    alignmentPath: [],
    learnerDurationMs: lDuration * 1e3,
    nativeDurationMs: nDuration * 1e3
  };
}
var AudioRecorder = class {
  mediaRecorder = null;
  audioContext;
  constructor(audioContext) {
    this.audioContext = audioContext;
  }
  start(stream) {
    const chunks = [];
    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
    const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : void 0);
    this.mediaRecorder = mediaRecorder;
    return new Promise((resolve, reject) => {
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        try {
          const blob = new Blob(chunks, { type: mediaRecorder.mimeType || "audio/webm" });
          const arrayBuffer = await blob.arrayBuffer();
          const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
          resolve(audioBuffer);
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      };
      mediaRecorder.onerror = (event) => {
        reject(new Error(`MediaRecorder error: ${event}`));
      };
      mediaRecorder.start(100);
    });
  }
  stop() {
    if (this.mediaRecorder?.state === "recording") {
      this.mediaRecorder.stop();
    }
  }
  isRecording() {
    return this.mediaRecorder?.state === "recording";
  }
};
var nativePitchCache = /* @__PURE__ */ new Map();
function getNativePitchContour(phraseId, audioBuffer) {
  const cached = nativePitchCache.get(phraseId);
  if (cached) return cached;
  const contour = extractPitchContour(audioBuffer);
  nativePitchCache.set(phraseId, contour);
  return contour;
}

// src/audio/vadSummary.ts
var EMPTY_MASTERY = () => ({ acquisition: 0, consolidating: 0, confident: 0, mastered: 0 });
function mean(values) {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}
function median(values) {
  if (values.length === 0) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}
function summariseVad(learnerIds, names, metricsByLearner, prosodyByLearner, prosodyAvailable = true) {
  const mastery = EMPTY_MASTERY();
  const learnerLatencies = [];
  const rows = [];
  let legoSeries = 0;
  let withData = 0;
  let withProsody = 0;
  let peakSum = 0, peakBase = 0;
  let avgSum = 0, avgBase = 0;
  let peakCountSum = 0, peakCountBase = 0;
  let promptStarts = 0, promptBase = 0;
  let overruns = 0, overrunBase = 0;
  let prosodyEvents = 0;
  for (const learnerId of learnerIds) {
    const metrics = metricsByLearner.get(learnerId) ?? [];
    const prosody = prosodyByLearner.get(learnerId) ?? null;
    if (metrics.length > 0) withData++;
    if (prosody && prosody.events > 0) withProsody++;
    if (metrics.length === 0 && !prosody) continue;
    legoSeries += metrics.length;
    for (const m of metrics) {
      if (m.mastery_state in mastery) mastery[m.mastery_state]++;
    }
    const latencies = metrics.map((m) => m.mean_latency_ms).filter((v) => typeof v === "number" && Number.isFinite(v));
    const learnerMean = mean(latencies);
    if (learnerMean !== null) learnerLatencies.push(learnerMean);
    if (prosody) {
      prosodyEvents += prosody.events;
      peakSum += prosody.peakEnergyDbSum;
      peakBase += prosody.peakEnergyDbBase;
      avgSum += prosody.averageEnergyDbSum;
      avgBase += prosody.averageEnergyDbBase;
      peakCountSum += prosody.peakCountSum;
      peakCountBase += prosody.peakCountBase;
      promptStarts += prosody.startedDuringPrompt;
      promptBase += prosody.startedDuringPromptBase;
      overruns += prosody.stillSpeakingAtVoice1;
      overrunBase += prosody.stillSpeakingAtVoice1Base;
    }
    const lastSeen = metrics.map((m) => m.last_seen_at).filter((v) => !!v).sort().pop() ?? null;
    rows.push({
      learnerId,
      name: names.get(learnerId) ?? "Unnamed learner",
      legos: metrics.length,
      mastered: metrics.filter((m) => m.mastery_state === "mastered").length,
      meanLatency: learnerMean,
      prosodyEvents: prosody?.events ?? 0,
      lastSeenAt: lastSeen
    });
  }
  rows.sort((a, b) => b.legos - a.legos || a.name.localeCompare(b.name));
  return {
    total: learnerIds.length,
    withData,
    withProsody,
    uptake: learnerIds.length > 0 ? withData / learnerIds.length : null,
    mastery,
    legoSeries,
    learnerLatencies,
    medianLatency: median(learnerLatencies),
    prosody: {
      events: prosodyEvents,
      learners: withProsody,
      meanPeakEnergyDb: peakBase > 0 ? peakSum / peakBase : null,
      meanAverageEnergyDb: avgBase > 0 ? avgSum / avgBase : null,
      meanPeakCount: peakCountBase > 0 ? peakCountSum / peakCountBase : null,
      startedDuringPromptRate: promptBase > 0 ? promptStarts / promptBase : null,
      startedDuringPromptBase: promptBase,
      stillSpeakingRate: overrunBase > 0 ? overruns / overrunBase : null,
      stillSpeakingBase: overrunBase,
      available: prosodyAvailable
    },
    learners: rows
  };
}

export { AudioRecorder, CONTOUR_MAX_POINTS, DEFAULT_CONTINUOUS_VAD_CONFIG, ENVELOPE_EXTRACTOR_CONSTANTS, SpeechTimingAnalyzer, VoiceActivityDetector, compareProsody, createEmptySpeechTimingResult, createSpeechTimingAnalyzer, createVoiceActivityDetector, extractEnvelopeMetadata, extractPitchContour, getNativePitchContour, median, summariseVad };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map